import {
  ApiEmployeeBusyIntervals,
  ApiEventBusyInterval,
  ApiEventsBusyFilter,
} from "@/types/event.types.ts";
import { addDays, format, startOfDay } from "date-fns";
import { formatInTimeZone, fromZonedTime, toZonedTime } from "date-fns-tz";

export function getAvailabilityDay(day: Date, timezone: string) {
  return {
    start: fromZonedTime(startOfDay(day), timezone),
    end: fromZonedTime(startOfDay(addDays(day, 1)), timezone),
  };
}

export function getBusyFilter(
  employeeIds: number[],
  start: Date,
  end: Date,
  timezone: string
): ApiEventsBusyFilter {
  const day = getAvailabilityDay(start, timezone);
  const endInstant = fromZonedTime(end, timezone);
  const lastInstant = new Date(Math.max(day.end.getTime(), endInstant.getTime()) - 1);
  return {
    employee_ids: [...new Set(employeeIds)].sort((a, b) => a - b),
    date_start: day.start.toISOString().slice(0, 10),
    date_end: lastInstant.toISOString().slice(0, 10),
  };
}

export function overlaps(interval: ApiEventBusyInterval, start: Date, end: Date) {
  return (
    new Date(interval.datetime_start).getTime() < end.getTime() &&
    new Date(interval.datetime_end).getTime() > start.getTime()
  );
}

export function getConflictingEmployees(busy: ApiEmployeeBusyIntervals[], start: Date, end: Date) {
  return busy.filter((person) =>
    person.intervals.some((interval) => overlaps(interval, start, end))
  );
}

export function suggestAvailableTimes(
  selectedTime: Date,
  timezone: string,
  durationMinutes: number,
  busy: ApiEmployeeBusyIntervals[],
  now = new Date()
) {
  if (!busy.length || durationMinutes <= 0) return [];
  const date = format(selectedTime, "yyyy-MM-dd");
  const preferredStart = fromZonedTime(selectedTime, timezone).getTime();
  const start = fromZonedTime(`${date}T09:00:00`, timezone).getTime();
  const end = fromZonedTime(`${date}T20:00:00`, timezone).getTime();
  const step = 15 * 60_000;
  const duration = durationMinutes * 60_000;
  const candidates: number[] = [];
  for (let time = start; time + duration <= end; time += step) {
    if (time < now.getTime()) continue;
    if (getConflictingEmployees(busy, new Date(time), new Date(time + duration)).length) continue;
    candidates.push(time);
  }
  candidates.sort((a, b) => Math.abs(a - preferredStart) - Math.abs(b - preferredStart) || b - a);

  const suggestions: number[] = [];
  for (const time of candidates) {
    if (suggestions.some((chosen) => time < chosen + duration && time + duration > chosen))
      continue;
    suggestions.push(time);
    if (suggestions.length === 3) break;
  }
  return suggestions.map((time) => new Date(time));
}

export function getTimelineBlock(interval: ApiEventBusyInterval, day: Date, timezone: string) {
  const bounds = getAvailabilityDay(day, timezone);
  if (!overlaps(interval, bounds.start, bounds.end)) return null;
  const start = new Date(
    Math.max(new Date(interval.datetime_start).getTime(), bounds.start.getTime())
  );
  const end = new Date(Math.min(new Date(interval.datetime_end).getTime(), bounds.end.getTime()));
  const localStart = toZonedTime(start, timezone);
  const localEnd = toZonedTime(end, timezone);
  const startMinute =
    start.getTime() === bounds.start.getTime()
      ? 0
      : localStart.getHours() * 60 + localStart.getMinutes();
  const endMinute =
    end.getTime() === bounds.end.getTime()
      ? 1440
      : localEnd.getHours() * 60 + localEnd.getMinutes();
  const length =
    endMinute > startMinute ? endMinute - startMinute : (end.getTime() - start.getTime()) / 60_000;
  return {
    top: (startMinute / 1440) * 100,
    height: (Math.min(length, 1440 - startMinute) / 1440) * 100,
  };
}

export function getGmtLabel(instant: Date, timezone: string) {
  const offset = formatInTimeZone(instant, timezone, "xxx");
  return `GMT${offset.replace(/^([+-])0/, "$1").replace(/:00$/, "")}`;
}
